import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { Card, Alert, Button, Input } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Connection Categories admin — full CRUD over app_connection_categories (DB rows,
 * seeded once from the registry, then user-owned). Relational model: providers hold a
 * category_id, so a category's provider list here is DERIVED (read-only) — move a
 * provider between categories on the Connection Providers page. `key` is immutable
 * after create (app_dynamic_tools.connection_category and connections' denormalized
 * category_key reference it as a plain string); the server blocks deleting a category
 * that still has providers or referencing dynamic tools with a 409.
 */
function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

const BLANK = { key: '', name: '', icon: '', description: '', sort: 0, is_active: true };

function CategoryForm({ editing, onSaved, onCancel }) {
  const { fetchApi } = useAppApi();
  const isNew = !editing.id;
  const [form, setForm] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    const data = {
      name: form.name, icon: form.icon || null, description: form.description || null,
      sort: Number(form.sort) || 0, is_active: !!form.is_active,
    };
    try {
      if (isNew) await fetchApi('connectionCategoryCreate', { ...data, key: form.key });
      else await fetchApi('connectionCategoryUpdate', { id: form.id, ...data });
      onSaved();
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="font-semibold">{isNew ? 'New category' : `Edit "${editing.name}"`}</div>
      {err && <Alert variant="error" title="Could not save">{err}</Alert>}
      <div className="grid grid-cols-2 gap-3">
        <Input label="Key (slug, immutable after create)" value={form.key || ''} disabled={!isNew}
          placeholder="e.g. messaging" onChange={(e) => setField('key', e.target.value)} />
        <Input label="Name" value={form.name || ''} onChange={(e) => setField('name', e.target.value)} />
        <Input label="Icon (lucide name)" value={form.icon || ''} onChange={(e) => setField('icon', e.target.value)} />
        <Input label="Sort" type="number" value={form.sort ?? 0} onChange={(e) => setField('sort', e.target.value)} />
        <div className="col-span-2">
          <Input label="Description" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
        </div>
      </div>
      <p className="text-xs text-secondary-500">Providers are attached from the Connection Providers page — each provider picks its category there.</p>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={!!form.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
        </label>
        <div className="flex-1" />
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving || !form.name || (isNew && !form.key)}>{saving ? 'Saving…' : 'Save'}</Button>
      </div>
    </Card>
  );
}

function CategoriesTable({ categories, providerSpecs, onEdit, onDelete, loading, error, onReload }) {
  const providersIn = (categoryKey) => providerSpecs.filter((p) => p.category === categoryKey).map((p) => p.label);
  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (cat) => (
        <div>
          <button onClick={() => nav(`/app-connection-category/${cat.id}`)} className="font-medium text-primary-600 hover:underline text-left">{cat.name}</button>
          <div className="text-secondary-500 text-xs max-w-xs truncate" title={cat.description}>{cat.description}</div>
        </div>
      ),
    },
    { key: 'key', label: 'Key', render: (cat) => <span className="font-mono text-xs">{cat.key}</span> },
    { key: 'providers', label: 'Providers (derived)', sortable: false, searchable: false, render: (cat) => <span className="text-xs">{providersIn(cat.key).join(', ') || '—'}</span> },
    { key: 'sort', label: 'Sort', searchable: false },
    {
      key: 'is_active',
      label: 'Status',
      searchable: false,
      render: (cat) => (
        <span className={`px-2 py-0.5 rounded text-xs ${cat.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
          {cat.is_active ? 'active' : 'disabled'}
        </span>
      ),
    },
    {
      key: 'row_actions',
      label: '',
      sortable: false,
      searchable: false,
      render: (cat) => (
        <Can module="connections" action="author" scope="all">
          <div className="whitespace-nowrap text-right">
            <button type="button" title="Edit" onClick={() => onEdit(cat)} className="p-1 text-primary-600 hover:bg-primary-50 rounded"><Pencil size={14} /></button>
            <button type="button" title="Delete" onClick={() => onDelete(cat)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
          </div>
        </Can>
      ),
    },
  ];
  return (
    <DataTable
      data={categories}
      columns={columns}
      loading={loading}
      error={error}
      emptyText="No categories yet."
      search={{ placeholder: 'Search categories…', keys: ['name', 'description', 'key'] }}
      onReload={onReload}
    />
  );
}

export default function ConnectionCategoriesPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [providerSpecs, setProviderSpecs] = useState([]);
  const [editing, setEditing] = useState(null); // BLANK-shaped form state, or null
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = () => Promise.all([
    fetchApi('crud', { model: 'AppConnectionCategory', action: 'list', orderBy: { column: 'sort', direction: 'asc' }, limit: 100 })
      .then((res) => setCategories((Array.isArray(res) ? res : res?.data) || [])),
    fetchApi('connectionProviders').then((spec) => setProviderSpecs(Object.values(spec.providers))),
  ]);

  useEffect(() => {
    load().catch((ex) => setErr(apiError(ex))).finally(() => setLoading(false));
  }, []);

  const onSaved = () => { setEditing(null); setErr(''); load().catch((ex) => setErr(apiError(ex))); };
  const onDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Providers and tools using it block deletion.`)) return;
    try {
      await fetchApi('connectionCategoryDelete', cat.id);
      onSaved();
    } catch (ex) {
      setErr(apiError(ex));
    }
  };

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Connection Categories"
        subtitle={<>{categories.length} categor{categories.length === 1 ? 'y' : 'ies'} — dynamic tools attach connections by category key; each provider picks its category on the Providers page.</>}
        actions={(
          <Can module="connections" action="author" scope="all">
            <Button size="sm" onClick={() => setEditing({ ...BLANK })}><Plus size={14} className="mr-1 inline" /> New category</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}
      {editing && <CategoryForm editing={editing} onSaved={onSaved} onCancel={() => setEditing(null)} />}
      <CategoriesTable
        categories={categories}
        providerSpecs={providerSpecs}
        onEdit={setEditing}
        onDelete={onDelete}
        loading={loading}
        onReload={() => load().catch((ex) => setErr(apiError(ex)))}
      />
    </PageShell>
  );
}
